import React, { useState, ReactNode } from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'

import {
  Autocomplete,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Textfield
} from '@clientbase/clientbase-library'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'

import CanLogical from 'components/CanLogical'
import IconContainer from 'components/IconContainer/IconContainer'

import { theme } from 'styles/theme'

type AutocompleteAddProps<T extends FieldValues, O = any> = {
  setValue?: UseFormSetValue<T>
  value: O
  onChange: any
  errorMessage?: string
  options: O[]
  addButtonText: string
  modalBodyComponent: ReactNode
  placeholder: string
  // ReactNode e não string: alguns campos penduram uma ressalva com tooltip no
  // rótulo (ex.: a conta da baixa manual), como o Textfield da lib já permite.
  label: ReactNode
  modalTitle?: string
  getOptionLabel?: (option: O) => string
  renderOption?: (props: any, option: O) => ReactNode
  getOptionDisabled?: (option: unknown) => boolean
  closeModalFunctionName?: string
  setValuePropName?: string
  showAddButton?: boolean
  /**
   * Permissão RBAC exigida para criar o item pelo autocomplete.
   * Quando informada, o botão de adicionar só aparece para quem a possui
   * (o endpoint de create já é barrado no backend pelo enforce_permissions).
   */
  addPermission?: string
  externalControllers?: {
    handleOpenModal: () => void
    handleCloseModal: () => void
    open: boolean
  }
}

const AutocompleteAdd = <T extends FieldValues, O = any>({
  value,
  onChange,
  errorMessage,
  setValue,
  options,
  addButtonText = 'Adicionar item',
  modalTitle,
  modalBodyComponent,
  getOptionLabel = (option: O) => (option as any).label || '',
  renderOption,
  getOptionDisabled,
  closeModalFunctionName = 'handleClose',
  setValuePropName = 'setValueFromOutside',
  placeholder,
  label,
  showAddButton = true,
  addPermission,
  externalControllers
}: AutocompleteAddProps<T, O>) => {
  const [modalCreateOpen, setModalCreateOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const isMobile = useMediaQuery('(max-width: 960px)')
  const hasAddPermission = CanLogical({ permission: addPermission })
  const canAdd = showAddButton && (!addPermission || !!hasAddPermission)

  const completeOptions = canAdd
    ? [
        {
          label: '',
          value: 'new'
        } as any,
        ...options
      ]
    : options

  const defaultRenderOption = (props: any, option: any) => {
    const isNoOptions = props.style?.width === '100%'
    if (option.value === 'new' && canAdd) {
      return (
        <Box
          key="new-item"
          {...props}
          onClick={() => {
            if (externalControllers) {
              externalControllers?.handleOpenModal()
            } else {
              setModalCreateOpen(true)
            }
          }}
          sx={{
            width: '100%',
            ...(!isNoOptions && {
              borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
              mt: '-8px'
            }),
            ...(isNoOptions && {
              mt: '-3px'
            })
          }}
        >
          <Stack width="100%">
            <Button
              fullWidth
              variantButton="ghost"
              sx={{ justifyContent: 'flex-start' }}
            >
              <Stack direction="row" gap="5px" alignItems="center">
                <Icon
                  icon="add"
                  sx={{
                    color: theme.palette.neutral[400],
                    fontSize: '18px'
                  }}
                />
                <Typography
                  sx={{
                    color: theme.palette.neutral[400],
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  {`${addButtonText}${
                    isNoOptions && inputValue ? ` "${inputValue}"` : ''
                  }`}
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </Box>
      )
    }
    return renderOption ? (
      renderOption(props, option)
    ) : (
      <Box component="li" {...props} key={option.value}>
        {getOptionLabel(option)}
      </Box>
    )
  }

  return (
    <>
      <Autocomplete
        value={value}
        onChange={onChange}
        options={completeOptions}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={
          getOptionLabel as ((option: unknown) => string) | undefined
        }
        componentsProps={{
          popper: {
            sx: {
              backgroundColor: 'black',
              borderRadius: '5px',
              boxShadow: `0px -1px 4px rgba(0, 0, 0, 0.3)`
            }
          }
        }}
        renderOption={defaultRenderOption}
        noOptionsText={
          !canAdd
            ? 'Nenhuma opção encontrada'
            : defaultRenderOption(
                {
                  style: { width: '100%' },
                  onClick: () => setModalCreateOpen(true)
                },
                { value: 'new', label: '' }
              )
        }
        renderInput={(params) => (
          <Textfield
            {...params}
            helperText={errorMessage}
            error={!!errorMessage}
            placeholder={placeholder}
            name="item"
            label={showAddButton ? label : ''}
            onChange={(e) => {
              params.inputProps.onChange?.(e as any)
              setInputValue(e.target.value)
            }}
          />
        )}
      />

      <Modal
        handleClose={() => {
          setModalCreateOpen(false), setInputValue('')
        }}
        open={!externalControllers ? modalCreateOpen : externalControllers.open}
        {...(!isMobile ? { height: 'auto !important' } : {})}
      >
        <ModalBody>
          <ModalHeader onClose={() => setModalCreateOpen(false)}>
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon="add" />
              <Typography variant="mdBold">{modalTitle || 'Criar'}</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent>
            <Box
              {...(isMobile ? { sx: { height: '94%', display: 'flex' } } : {})}
            >
              {modalBodyComponent &&
                React.cloneElement(modalBodyComponent as React.ReactElement, {
                  [closeModalFunctionName]: () => setModalCreateOpen(false),
                  [setValuePropName]: setValue,
                  defaultName: inputValue
                })}
            </Box>
          </ModalContent>
        </ModalBody>
      </Modal>
    </>
  )
}

export default AutocompleteAdd
